export type AbilityScore = 'not_yet' | 'partially' | 'yes';

export type AbilityCheck = {
  abilityId: string;
  text: string; // snapshot of the wording at scoring time
  score?: AbilityScore; // undefined = not yet scored by the teacher
};

/** One class week of homework, as stored by the agent-events worker. */
export type HomeworkWeek = {
  /** Monday (UTC) of the week, YYYY-MM-DD. */
  weekStart: string;
  totalExercises: number;
  submittedExercises: number;
  percentage: number;
};

export type FeedbackContent = {
  strengths: string[];
  attentionAreas: string[];
  recommendations: string[];
  skillsLearned: string[];
  classThemes: string[];
  engagementStats: {
    presence: { percentage: number; color: string };
    /** `weeks` exists only on reports generated after Oct 2026; older ones show the ring. */
    submissions: { percentage: number; color: string; weeks?: HomeworkWeek[] };
    extraClassPresence: { percentage: number; color: string };
  };
  growthNarrative: string;
  abilityChecks?: AbilityCheck[]; // present only for final feedback
};

/**
 * Returns the feedback content with the end-of-term Ability Check grid seeded
 * from the level's ability catalog, when appropriate. Pure and idempotent:
 *
 * - only seeds `final` feedback,
 * - never overwrites existing `abilityChecks` (the snapshot is taken once),
 * - skips when the catalog is empty/absent (section stays hidden).
 *
 * This is computed from (content, type, catalog) together so the seed applies
 * regardless of which arrives last — a `null` content that later populates, or
 * a catalog that loads before the content does.
 */
export function seedAbilityChecks(
  content: FeedbackContent | null,
  type: string | null | undefined,
  catalog: ReadonlyArray<{ abilityId: string; text: string }> | undefined,
): FeedbackContent | null {
  if (!content) return content;
  if (type !== 'final') return content;
  if (content.abilityChecks) return content;
  if (!catalog || catalog.length === 0) return content;
  return {
    ...content,
    abilityChecks: catalog.map((ability) => ({
      abilityId: ability.abilityId,
      text: ability.text,
    })),
  };
}

export function parseFeedbackContent(raw: string | null): FeedbackContent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Backward compat: rename digital -> extraClassPresence
    if (parsed.engagementStats?.digital && !parsed.engagementStats?.extraClassPresence) {
      parsed.engagementStats.extraClassPresence = parsed.engagementStats.digital;
      delete parsed.engagementStats.digital;
    }
    // abilityChecks passes through unchanged (no migration needed).
    return parsed as FeedbackContent;
  } catch {
    return null;
  }
}
